/**
 * AutopostInsights — Phase 3.C Shadow Insights dashboard (READ-ONLY).
 *
 * Surfaces the calibration engine's three core outputs plus the
 * dispatcher's audit-log summary. No mutations, no policy edits —
 * users who want to change tenant policy go to /autopost-policy.
 *
 * Endpoints consumed:
 *   GET /api/autopost/insights
 *   GET /api/autopost/calibration/curve
 *   GET /api/autopost/calibration/overrides
 *   GET /api/autopost/calibration/stability
 */
import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Activity, TrendingUp, AlertTriangle, ShieldAlert,
  Sparkles, RefreshCcw, Layers
} from 'lucide-react';

export default function AutopostInsights({ user }) {
  const [windowDays, setWindowDays] = useState(14);
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState(null);
  const [curve, setCurve] = useState(null);
  const [overrides, setOverrides] = useState(null);
  const [stability, setStability] = useState(null);

  const isPrivileged = ['admin', 'accountant', 'cfo'].includes((user?.role || '').toLowerCase());

  const loadAll = async () => {
    setLoading(true);
    try {
      const [i, c, o, s] = await Promise.all([
        axiosInstance.get(`/autopost/insights?window_days=${windowDays}`),
        axiosInstance.get(`/autopost/calibration/curve?window_days=${windowDays}`),
        axiosInstance.get(`/autopost/calibration/overrides?window_days=${windowDays}&min_confidence=0.95`),
        axiosInstance.get(`/autopost/calibration/stability?window_days=${windowDays}`),
      ]);
      setInsights(i.data);
      setCurve(c.data);
      setOverrides(o.data);
      setStability(s.data);
    } catch (_) {
      toast.error('Failed to load Autopost Insights');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, [windowDays]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!isPrivileged) {
    return (
      <div className="p-8 text-center text-sm text-gray-500">
        Admin, Accountant or CFO access required.
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="autopost-insights-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Sparkles size={22} className="text-indigo-600" />
            Autopost Shadow Insights
          </h1>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Read-only measurement layer. Reports how well the AI suggestion + policy
            engine would behave if Autopost flipped live. No automation runs from this page.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="autopost-window-select"
            value={windowDays}
            onChange={(e) => setWindowDays(Number(e.target.value))}
            className="text-sm border border-gray-200 rounded-md px-3 py-2 bg-white"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            data-testid="autopost-refresh-btn"
            onClick={loadAll}
            className="inline-flex items-center gap-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-md"
          >
            <RefreshCcw size={14} /> Refresh
          </button>
        </div>
      </div>

      {loading && <div className="text-sm text-gray-400 py-12 text-center">Loading…</div>}

      {!loading && (
        <>
          <ShadowSummary insights={insights} />
          <CalibrationCurve curve={curve} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
            <OverrideMap overrides={overrides} />
            <CategoryStability stability={stability} />
          </div>
        </>
      )}
    </div>
  );
}


// ───────────────────────────────────────────────────────────────────────
// Summary cards
// ───────────────────────────────────────────────────────────────────────
function ShadowSummary({ insights }) {
  const i = insights || {};
  const cards = [
    {
      label: 'Total Shadow Decisions',
      value: (i.total ?? 0).toLocaleString(),
      icon: Layers, color: 'indigo',
      testId: 'card-total',
    },
    {
      label: 'Would-have Auto-Posted',
      value: `${((i.would_have_auto_posted_pct || 0) * 100).toFixed(1)}%`,
      sub:   `${(i.would_have_auto_posted ?? 0).toLocaleString()} rows`,
      icon: TrendingUp, color: 'emerald',
      testId: 'card-would-have-auto-posted',
    },
    {
      label: 'Avg Confidence',
      value: ((i.avg_confidence ?? 0) * 100).toFixed(1) + '%',
      icon: Activity, color: 'sky',
      testId: 'card-avg-confidence',
    },
    {
      label: 'Shadow Mode',
      value: 'LOG_ONLY',
      sub:   'Hub untouched',
      icon: ShieldAlert, color: 'amber',
      testId: 'card-shadow-mode',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((c, idx) => {
        const Icon = c.icon;
        return (
          <div
            key={idx}
            data-testid={c.testId}
            className={`rounded-lg border border-${c.color}-100 bg-${c.color}-50/40 p-4`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{c.label}</span>
              <Icon size={16} className={`text-${c.color}-600`} />
            </div>
            <div className="text-2xl font-semibold text-gray-900">{c.value}</div>
            {c.sub && <div className="text-xs text-gray-400 mt-1">{c.sub}</div>}
          </div>
        );
      })}
    </div>
  );
}


// ───────────────────────────────────────────────────────────────────────
// Calibration Curve — confidence vs accept-rate
// ───────────────────────────────────────────────────────────────────────
function CalibrationCurve({ curve }) {
  const bands = curve?.bands || [];
  const totalSample = curve?.total_sample ?? 0;

  return (
    <div className="mt-6 rounded-lg border border-gray-200 bg-white p-5" data-testid="calibration-curve">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <Activity size={16} className="text-indigo-600" />
            Confidence Calibration Curve
          </h2>
          <p className="text-xs text-gray-400 mt-1">
            Drift = actual accept rate − band midpoint. Positive ⇒ AI under-confident;
            negative ⇒ AI over-confident. Sample: {totalSample.toLocaleString()} rows.
          </p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-gray-500 border-b border-gray-100">
            <tr>
              <th className="text-left py-2 px-2">Band</th>
              <th className="text-right py-2 px-2">Sample</th>
              <th className="text-right py-2 px-2">Accepted</th>
              <th className="text-right py-2 px-2">Accept Rate</th>
              <th className="text-right py-2 px-2">95% CI</th>
              <th className="text-right py-2 px-2">Drift</th>
            </tr>
          </thead>
          <tbody>
            {bands.map((b) => {
              const drift = b.drift;
              const driftClass = drift == null ? 'text-gray-300'
                : drift > 0.05 ? 'text-emerald-600'
                : drift < -0.05 ? 'text-rose-600' : 'text-gray-500';
              return (
                <tr key={b.min_confidence} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="py-2 px-2 font-mono text-xs text-gray-600">
                    [{b.min_confidence.toFixed(2)}, {b.max_confidence.toFixed(2)})
                  </td>
                  <td className="py-2 px-2 text-right">{b.sample.toLocaleString()}</td>
                  <td className="py-2 px-2 text-right">{b.accepted.toLocaleString()}</td>
                  <td className="py-2 px-2 text-right">
                    {b.accept_rate == null ? '—' : `${(b.accept_rate * 100).toFixed(1)}%`}
                  </td>
                  <td className="py-2 px-2 text-right text-xs text-gray-400">
                    {b.ci_low == null ? '—' :
                      `[${(b.ci_low*100).toFixed(0)}, ${(b.ci_high*100).toFixed(0)}]`}
                  </td>
                  <td className={`py-2 px-2 text-right font-medium ${driftClass}`}>
                    {drift == null ? '—' : (drift >= 0 ? '+' : '') + (drift*100).toFixed(1) + '%'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ───────────────────────────────────────────────────────────────────────
// Override Map — high-confidence rejects/edits
// ───────────────────────────────────────────────────────────────────────
function OverrideMap({ overrides }) {
  const items = overrides?.items || [];
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5" data-testid="override-map">
      <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
        <AlertTriangle size={16} className="text-rose-600" />
        Human Override Map (≥ 95% confidence)
      </h2>
      <p className="text-xs text-gray-400 mt-1 mb-3">
        Where the AI was very confident but the user disagreed. These are the
        most actionable signals for tightening policy.
      </p>
      {items.length === 0 ? (
        <div className="text-xs text-gray-400 py-6 text-center">
          No high-confidence overrides in the window — model is aligned with users.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-xs text-gray-500 border-b border-gray-100">
            <tr>
              <th className="text-left py-2">Classifier</th>
              <th className="text-left py-2">Suggested</th>
              <th className="text-right py-2">Edits</th>
              <th className="text-right py-2">Rejects</th>
              <th className="text-right py-2">Avg Conf.</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r, idx) => (
              <tr key={idx} className="border-b border-gray-50 hover:bg-gray-50/50">
                <td className="py-2 font-mono text-xs text-gray-500">{r.classifier_type}</td>
                <td className="py-2">{r.suggested_value || '—'}</td>
                <td className="py-2 text-right">{r.edits}</td>
                <td className="py-2 text-right text-rose-600">{r.rejects}</td>
                <td className="py-2 text-right text-xs text-gray-500">
                  {((r.avg_confidence || 0) * 100).toFixed(1)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}


// ───────────────────────────────────────────────────────────────────────
// Category Stability
// ───────────────────────────────────────────────────────────────────────
function CategoryStability({ stability }) {
  const items = stability?.items || [];
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5" data-testid="category-stability">
      <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
        <Layers size={16} className="text-amber-600" />
        Category Stability Score
      </h2>
      <p className="text-xs text-gray-400 mt-1 mb-3">
        Per-category accept rate + Wilson 95% CI. Unstable = low sample,
        low acceptance, or wide statistical uncertainty.
      </p>
      {items.length === 0 ? (
        <div className="text-xs text-gray-400 py-6 text-center">
          No feedback data in the window yet. Use the AI suggestion field on
          expenses to start populating the calibration signal.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-xs text-gray-500 border-b border-gray-100">
            <tr>
              <th className="text-left py-2">Category</th>
              <th className="text-right py-2">Sample</th>
              <th className="text-right py-2">Accept Rate</th>
              <th className="text-right py-2">CI Width</th>
              <th className="text-center py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.category} className="border-b border-gray-50 hover:bg-gray-50/50">
                <td className="py-2">{r.category || '—'}</td>
                <td className="py-2 text-right">{r.sample}</td>
                <td className="py-2 text-right">{((r.accept_rate || 0) * 100).toFixed(1)}%</td>
                <td className="py-2 text-right text-xs text-gray-400">
                  {((r.ci_width || 0) * 100).toFixed(0)}%
                </td>
                <td className="py-2 text-center">
                  <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full ${
                    r.unstable
                      ? 'bg-amber-50 text-amber-700 border border-amber-200'
                      : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  }`}>
                    {r.unstable ? 'Unstable' : 'Stable'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
